import { z } from 'zod';

// ============================================================================
// Bulk Cancel (orders or tickets)
// ============================================================================

export const BulkCancelSchema = z.object({
  ids: z.array(z.string()).min(1).max(100),
  reason: z.string().max(500).optional(),
});
export type BulkCancelInput = z.infer<typeof BulkCancelSchema>;

// ============================================================================
// Bulk Resend (tickets)
// ============================================================================

export const BulkResendSchema = z.object({
  ids: z.array(z.string()).min(1).max(100),
});
export type BulkResendInput = z.infer<typeof BulkResendSchema>;

// ============================================================================
// Bulk Export (orders)
// ============================================================================

export const BulkExportSchema = z.object({
  ids: z.array(z.string()).min(1).max(1000),
  format: z.enum(['csv', 'xlsx', 'pdf']),
});
export type BulkExportInput = z.infer<typeof BulkExportSchema>;

// ============================================================================
// Response Schemas
// ============================================================================

export const BulkCancelResponseSchema = z.object({
  success: z.number().int(),
  failed: z.number().int(),
  failedIds: z.array(z.string()),
  reason: z.string(),
});
export type BulkCancelResponse = z.infer<typeof BulkCancelResponseSchema>;

export const BulkResendResponseSchema = z.object({
  success: z.number().int(),
  failed: z.number().int(),
  failedIds: z.array(z.string()),
  ticketNumbers: z.array(z.string()),
});
export type BulkResendResponse = z.infer<typeof BulkResendResponseSchema>;

export const BulkExportResponseSchema = z.object({
  format: z.enum(['csv', 'xlsx', 'pdf']),
  totalRecords: z.number().int(),
  requestedIds: z.number().int(),
  exportedAt: z.string(),
  orders: z.array(z.object({
    id: z.string(),
    orderNumber: z.string(),
    status: z.string(),
    customerName: z.string(),
    customerEmail: z.string(),
    visitDate: z.string(),
    subtotal: z.number(),
    discount: z.number(),
    tax: z.number(),
    total: z.number(),
    paymentMethod: z.string(),
    sourceChannel: z.string(),
    ticketCount: z.number().int(),
    createdAt: z.string(),
    items: z.array(z.object({
      ticketTypeCode: z.string(),
      ticketTypeName: z.string(),
      quantity: z.number().int(),
      unitPrice: z.number(),
      subtotal: z.number(),
    })),
  })),

  /**
   * The rendered export, when the requested format is one this service produces.
   *
   * `format` above used to be echoed back from the request over a JSON body that was
   * the same whichever format was asked for — request `xlsx` and you got JSON labelled
   * `xlsx`. A caller writing that to `orders.xlsx` produced a file no spreadsheet
   * opens. Only `csv` is rendered today; `xlsx` and `pdf` are refused rather than
   * mislabelled.
   */
  content: z.string().optional(),
  contentType: z.string().optional(),
  filename: z.string().optional(),
});
export type BulkExportResponse = z.infer<typeof BulkExportResponseSchema>;
